import { formatRelativeTime } from "@/shared/utils/formatters";
import { toLegalRoleLabels } from "@/shared/utils/syncRoleColumns";

import type { ActionType } from "@/shared/types/activityLog";
import type {
  HighlightData,
  LawyerEstateRow,
  UserEstateRow,
  UserRow,
  EstateItemRow,
  EstateInvitationRow,
  EstateContactRow,
  ActivityLogRow,
} from "@/shared/types/lawyerDashboard";

const IMMEDIATE_FAMILY = ["spouse", "child", "sibling", "parent"];

export const LETTERS_DOC_TYPES = ["letters_testamentary", "letters_administration"];

function deriveHighlightFromActivity(log: ActivityLogRow): HighlightData {
  const actionType = log.action_type as ActionType;
  const metadata = log.metadata ?? {};
  const classification =
    typeof metadata.classification === "string" ? metadata.classification : null;
  const taskName = typeof metadata.task_name === "string" ? metadata.task_name : null;

  let label: string;
  let nextStep: string;

  if (actionType === "added_estate_item" && classification === "asset") {
    label = "New asset added";
    nextStep = "Review asset";
  } else if (actionType === "added_estate_item" && classification === "debt") {
    label = "New debt added";
    nextStep = "Review debt";
  } else if (actionType === "updated_estate_item" && classification === "asset") {
    label = "Asset updated";
    nextStep = "Review asset";
  } else if (actionType === "updated_estate_item" && classification === "debt") {
    label = "Debt updated";
    nextStep = "Review debt";
  } else if (actionType === "uploaded_document") {
    label = "New document added";
    nextStep = "Review document";
  } else if (actionType === "submitted_task" || actionType === "completed_task") {
    label = taskName ? `${taskName} submitted` : "Task submitted";
    nextStep = "See progress";
  } else if (actionType === "accepted_invitation") {
    label = "Invite accepted";
    nextStep = "See progress";
  } else {
    const formatted = actionType.replace(/_/g, " ");
    label = formatted.charAt(0).toUpperCase() + formatted.slice(1);
    nextStep = "View details";
  }

  return {
    category: "passive",
    action: null,
    label,
    nextStep,
    timeAgo: formatRelativeTime(log.created_at),
    timestamp: log.created_at,
  };
}

export function deriveHighlight(
  estateId: number,
  invitations: EstateInvitationRow[],
  contacts: EstateContactRow[],
  assignedAt: string | null,
  latestActivity: ActivityLogRow | undefined,
  plaidConnectionMap?: Map<number, { connected: boolean }>,
  lettersEstateIds?: Set<number>,
): HighlightData | null {
  const pendingInvite = invitations.find(
    (inv) => inv.estate_id === estateId && inv.status === "pending",
  );
  if (pendingInvite) {
    return {
      category: "action",
      action: "resend_invite",
      label: "Invite pending",
      nextStep: "Resend invite",
      timeAgo: formatRelativeTime(pendingInvite.created_at),
      timestamp: pendingInvite.created_at,
    };
  }

  const hasMissingContact = contacts.some(
    (c) =>
      c.estate_id === estateId &&
      c.relationship !== null &&
      IMMEDIATE_FAMILY.includes(c.relationship.toLowerCase()) &&
      !c.email &&
      !c.phone,
  );
  if (hasMissingContact) {
    return {
      category: "action",
      action: "nudge_client",
      label: "Contact missing",
      nextStep: "Nudge client",
      timeAgo: "",
      timestamp: assignedAt ?? "",
    };
  }

  if (latestActivity) {
    return deriveHighlightFromActivity(latestActivity);
  }

  const isAccountMissing = !plaidConnectionMap?.get(estateId)?.connected;
  const hasLetters = lettersEstateIds?.has(estateId) ?? false;
  if (isAccountMissing && hasLetters) {
    return {
      category: "action",
      action: "nudge_estate_account",
      label: "Estate account not connected",
      nextStep: "Nudge client",
      timeAgo: "",
      timestamp: assignedAt ?? "",
    };
  }

  return null;
}

export function deriveProceedingType(documentTypes: Set<string>): string {
  const hasWill = documentTypes.has("will");
  const hasTrust = documentTypes.has("trust") || documentTypes.has("certification_of_trust");

  if (hasWill && hasTrust) return "Probate & Trust";
  if (hasWill) return "Probate";
  if (hasTrust) return "Trust administration";
  return "Administration";
}

export function deriveAuthoritySuffix(documentTypes: Set<string>, hasLetters: boolean): string {
  const hasWill = documentTypes.has("will");
  const hasTrust = documentTypes.has("trust") || documentTypes.has("certification_of_trust");
  if (!hasWill && hasTrust) return "Trustee authority";
  if (hasLetters) return "Letters issued";
  return "Pre-letters";
}

export function resolveClientName(user: UserRow | undefined): string | null {
  if (!user) return null;
  return (
    user.full_name ??
    (user.first_name || user.last_name
      ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim()
      : null) ??
    user.email ??
    null
  );
}

export function computeInventory(items: EstateItemRow[], estateId: number) {
  let total = 0;
  let assetCount = 0;
  let liabilityCount = 0;

  for (const item of items) {
    if (item.estate_id !== estateId) continue;
    if (item.classification === "asset") {
      total += Math.abs(item.balance);
      assetCount += 1;
    } else if (item.classification === "debt") {
      total -= Math.abs(item.balance);
      liabilityCount += 1;
    }
  }

  return { inventoryTotal: total, assetCount, liabilityCount };
}

export function buildUserEstateMap(userEstates: UserEstateRow[]) {
  const map = new Map<number, UserEstateRow>();
  for (const ue of userEstates) {
    if (ue.estate_id !== null && !map.has(ue.estate_id)) {
      map.set(ue.estate_id, ue);
    }
  }
  return map;
}

export function buildLatestActivityMap(logs: ActivityLogRow[]) {
  const map = new Map<number, ActivityLogRow>();
  for (const log of logs) {
    if (!map.has(log.estate_id)) {
      map.set(log.estate_id, log);
    }
  }
  return map;
}

interface AssemblyContext {
  userEstateMap: Map<number, UserEstateRow>;
  ownerMap: Map<number, number>;
  userMap: Map<number, UserRow>;
  items: EstateItemRow[];
  invitations: EstateInvitationRow[];
  contacts: EstateContactRow[];
  activityMap: Map<number, ActivityLogRow>;
  docTypesByEstate: Map<number, Set<string>>;
  lettersEstateIds: Set<number>;
  plaidConnectionMap: Map<number, { connected: boolean }>;
}

interface RawAssignment {
  id: string;
  estate_id: number;
  assigned_at: string | null;
  person: Record<string, unknown> | Record<string, unknown>[] | null;
}

export function assembleRow(le: RawAssignment, ctx: AssemblyContext): LawyerEstateRow {
  const estateId = le.estate_id;
  const estate = (Array.isArray(le.person) ? le.person[0] : le.person) as
    | Record<string, unknown>
    | undefined;

  const primaryUserEstate = ctx.userEstateMap.get(estateId);
  const primaryUser =
    primaryUserEstate?.user_id != null ? ctx.userMap.get(primaryUserEstate.user_id) : undefined;

  const ownerId = ctx.ownerMap.get(estateId);
  const ownerUser = ownerId != null ? ctx.userMap.get(ownerId) : undefined;
  const resolvedUser = ownerUser ?? primaryUser;

  const resolvedUserEstate =
    resolvedUser && primaryUserEstate?.user_id === resolvedUser.user_id
      ? primaryUserEstate
      : undefined;

  const clientRole = resolvedUserEstate
    ? (toLegalRoleLabels({
        is_executor: resolvedUserEstate.is_executor,
        is_administrator: resolvedUserEstate.is_administrator,
        is_trustee: resolvedUserEstate.is_trustee,
        is_nominated: resolvedUserEstate.is_nominated,
      })[0] ?? null)
    : null;

  const { inventoryTotal, assetCount, liabilityCount } = computeInventory(ctx.items, estateId);
  const estateDocTypes = ctx.docTypesByEstate.get(estateId) ?? new Set<string>();

  return {
    id: le.id,
    estateId,
    assignedAt: le.assigned_at ?? null,
    clientName: resolveClientName(resolvedUser),
    clientEmail: resolvedUser?.email ?? null,
    clientRole,
    clientRelationship: resolvedUserEstate?.relationship_to_decedent ?? null,
    decedentName: (estate?.full_name as string) ?? null,
    dateOfDeath: (estate?.date_of_death as string) ?? null,
    proceedingType: deriveProceedingType(estateDocTypes),
    authoritySuffix: deriveAuthoritySuffix(estateDocTypes, ctx.lettersEstateIds.has(estateId)),
    highlight: deriveHighlight(
      estateId,
      ctx.invitations,
      ctx.contacts,
      le.assigned_at ?? null,
      ctx.activityMap.get(estateId),
      ctx.plaidConnectionMap,
      ctx.lettersEstateIds,
    ),
    inventoryTotal,
    assetCount,
    liabilityCount,
    location: (estate?.location as string) ?? null,
  };
}
